<script setup lang="ts">
import { ref, watch, computed } from "vue";
import { useRouter } from "vue-router";
import { useTranslation } from "i18next-vue";

import type { Headers, PaginationMeta } from "@/types/rest";
import type {
  ConfiguredEntity,
  IntegrationInstance,
  EntityFilterData
} from "@/types/integrationInstance";

import type { ChangeCallbackParams } from "@/types/config";
import type { Macro } from "@/types/macro";
import type { LanguageText } from "@/types/config";
import { EntityType } from "@/types/enums";

import { useComponentErrorCapture } from "@/mixins/error";
import { getNewIconName } from "@/mixins/icon";

import translatedProperty, {
  getCurrentLocale,
} from "@/mixins/translatedProperty";
import { useWindowDimension } from "@/mixins/windowDimension";
import { focusInput } from "@/mixins/device";
import { getPaginationLimit, savePaginationLimit } from "@/mixins/listing";

import { macrosStore } from "@/stores/macros";
import { integrationsStore } from "@/stores/integrations";
import { addErrorFull, addErrorBottom } from "@/stores/messages";

import Modal from "@/components/elements/Modal.vue";
import IconSelect from "@/components/elements/icon/IconSelect.vue";
import UCInput from "@/components/ui/UCInput.vue";
import EntityListFiltered from "@/components/elements/entity/EntityListFiltered.vue";

const { t } = useTranslation();

const router = useRouter();
const { isMobileScreen } = useWindowDimension();

const macrosStorage = macrosStore();
const integrationStorage = integrationsStore();

useComponentErrorCapture();

const props = defineProps({
  clone: {
    type: Object as () => Macro | null,
    default: null,
  },
});

defineExpose({
  open
});

const emit = defineEmits(["close"]);

const excludedEntityTypes = ref([EntityType.sensor]);

const macroSkeleton = {
  name: "",
  icon: "uc:macro",
  description: "",
};

const filterSkeleton = {
  searchText: '',
  entityTypes: '',
  instances: ''
};

const newMacro = ref(JSON.parse(JSON.stringify(macroSkeleton)));
const entityListFilter = ref<EntityFilterData>(JSON.parse(JSON.stringify(filterSkeleton)));

const filteredEntities = ref<ConfiguredEntity[]>([]);
const instances = ref<IntegrationInstance[]>([]);

const assignedEntities = ref<ConfiguredEntity[]>([]);

const activeStep = ref(1);
const slideRight = ref(false);
const showModal = ref(false);
const errorMessage = ref("");

const pagination = ref<PaginationMeta>({
  limit: getPaginationLimit() ?? 20,
  page: 1,
});

integrationStorage.$subscribe((mutation, state) => {
  if (state.instances) {
    instances.value = state.instances;
  }
});

watch(showModal, async (val) => {
  if (macroSkeleton) {
    newMacro.value = JSON.parse(JSON.stringify(macroSkeleton));
  }
  entityListFilter.value = JSON.parse(JSON.stringify(filterSkeleton));
  assignedEntities.value = [];
  if (val == true) {
    pagination.value = {
      limit: getPaginationLimit() ?? 20,
      page: 1,
    };
    fetchFilteredEntities(true);
    try {
      instances.value = await integrationStorage.getInstances();
    } catch (e: any) {
      addErrorBottom(e);
    }
  }

  if (props.clone) {
    newMacro.value.name = translatedProperty(props.clone.name);
    newMacro.value.description = translatedProperty(props.clone.description);

    let iconItem = 'uc:macro';

    if (props.clone.icon) {
      if (props.clone.icon.includes("uc:")) {
        const iconName = props.clone.icon.split("uc:")[1];
        const newIconName = await getNewIconName(iconName);

        if (iconName && iconName.length > 0 && newIconName && newIconName.length > 0) {
          iconItem = props.clone.icon.replace(iconName, newIconName);
        } else {
          iconItem = props.clone.icon;
        }
      } else {
        iconItem = props.clone.icon;
      }
    }

    newMacro.value.icon = iconItem;
  }
})

watch(activeStep, () => {
  const modalAddMacro = document.querySelector('.modal--add-macro') as HTMLElement;
  if (modalAddMacro) {
    focusInput(modalAddMacro, true);
  }
});

watch(
  () => pagination.value.limit,
  (val) => {
    savePaginationLimit(val);
  }
);

const stepTransition = computed(() => {
  return slideRight.value == true ? 'slide-tab-right' : 'slide-tab-left';
});

async function submitForm() {
  try {
    let macro;
    const nameValue: LanguageText = {
      [getCurrentLocale()]: newMacro.value.name,
    };
    
    const descriptionValue = {
      [getCurrentLocale()]: newMacro.value.description,
    };

    const iconRegex = /fa-/;
    const iconValue = newMacro.value.icon.replace(iconRegex, 'uc:');

    const entity_ids = assignedEntities.value.map((entity) => {
      return entity.entity_id;
    });

    if (props.clone) {
      macro = await macrosStorage.clone(
        {
          name: nameValue,
          description: descriptionValue,
          icon: iconValue,
        },
        props.clone.entity_id
      );
    } else {
      macro = await macrosStorage.create({
        name: nameValue,
        description: descriptionValue,
        icon: iconValue,
        options: {
          entity_ids: entity_ids
        }
      })
    }

    await macrosStorage.getAll();
    router.push({
      name: "macro",
      params: { macro_id: macro.entity_id },
    });
  }
  catch (e: any) {
    addErrorFull(e);
  }
  closeModal();
}

function changeFilter(data: EntityFilterData) {
  entityListFilter.value = data;
  fetchFilteredEntities(true);
}

function reloadEntities() {
  fetchFilteredEntities(true);
}

async function fetchFilteredEntities(userFetchFirstPage: boolean = false) {
  if (userFetchFirstPage === true) {
    pagination.value.page = 1;
  }

  let searchText = entityListFilter.value.searchText;
  let filterByEntityTypes = JSON.parse(JSON.stringify(entityListFilter.value.entityTypes));

  if (filterByEntityTypes.length < 1) {
    const necessaryEntityTypes = Object.values(EntityType)
    .filter(type => !excludedEntityTypes.value?.includes(type))
    .join(',');

    filterByEntityTypes = necessaryEntityTypes;
  }

  try {
    const entList = await integrationStorage.getConfiguredEntitiesByPageByLimit(entityListFilter.value.instances || '', false, pagination.value.page, pagination.value.limit, searchText, filterByEntityTypes);
    filteredEntities.value = entList.data.configuredEntities as ConfiguredEntity[];

    const listHeaders = entList.headers as Headers;
    if (listHeaders) {
      const headerLimit = Number(listHeaders['pagination-limit']);
      const newLimit = headerLimit <= Number(listHeaders['pagination-count']) ? pagination.value.limit : headerLimit;
      pagination.value = {
        count: Number(listHeaders['pagination-count']) || 0,
        limit: newLimit || 0,
        page: Number(listHeaders['pagination-page']) || 0,
      }
    }
  } catch (e) {
    console.error(e);
  }
}

function open() {
  showModal.value = true;
  activeStep.value = 1;
}

function closeModal() {
  showModal.value = false;
  activeStep.value = 1;
}

function goToStep(step: number) {
  clearErrors();
  if (step == 2 && newMacro.value.name.length < 1) {
    return errorMessage.value = t("ui.required_field");
  }

  if (step == 2 && props.clone) {
    return submitForm();
  }
  
  slideRight.value = step < activeStep.value ? true : false;
  activeStep.value = step;
}

async function changeMacroIcon(params: ChangeCallbackParams) {
  const { value } = params;
  if (newMacro.value) {
    newMacro.value.icon = value as string;
  }
}

function changeAssignedEntities(val: ConfiguredEntity[]) {
  assignedEntities.value = val;
}

function changePage(value: number) {
  pagination.value.page = value;
  fetchFilteredEntities();
}

function changePerPage(value: number) {
  pagination.value.page = 1;
  pagination.value.limit = value;
  fetchFilteredEntities();
}

function clearErrors() {
  if (errorMessage.value.length > 0) {
    errorMessage.value = '';
  }
}

</script>
<template>
  <Teleport to="body">
    <Modal
      :show="showModal"
      @close="closeModal"
      name="add-macro"
      class="modal--steps modal--add modal--add-macro">
      <template #header>
        <button
          v-show="activeStep == 2"
          @click="goToStep(1)"
          class="button button--tertiary button--icon modal__header__button-back"
          :class="{'button--secondary button--icon--small': isMobileScreen}">
          <i class="fa-light fa-arrow-left"></i>
        </button>
        <span class="modal__header__item--desktop">
          <template v-if="clone != null">{{ $t("macro.clone.clone_macro") }}</template>
          <template v-else>{{ $t("macro.add.add_new_macro") }}</template>
        </span>
        <span class="modal__header__item--phone">
          <template v-if="clone != null">{{ $t("macro.clone.clone_macro") }}</template>
          <template v-else>{{ $t("macro.add.new_macro") }}</template>
        </span>
      </template>
      <Transition :name="stepTransition">
        <div v-show="activeStep == 1" class="modal__body__step">
          <div class="modal__body__step__body modal--add__base-data" v-overflow-indicator>
            <IconSelect
              :key="newMacro.icon"
              :value="newMacro.icon"
              :change-callback="changeMacroIcon"
              :fallback="'fa-thin fa-list-alt'"
              :has-tv-channel="true"
            />
            <UCInput
              v-model="newMacro.name"
              @click="clearErrors"
              :label="$t('form.name')"
              :error-message="errorMessage ? $t(errorMessage) : ''"
              :full-w="true"
              :focus="true"
            />
            <UCInput
              v-model="(newMacro.description as string)"
              @click="clearErrors"
              :type="'textarea'"
              :label="$t('form.description')"
              :full-w="true"
            />
          </div>
        </div>
      </Transition>
      <Transition :name="stepTransition">
        <div v-show="activeStep == 2" class="modal__body__step">
          <Transition name="opacity">
            <button v-show="activeStep == 2" @click="goToStep(1)" class="button button--secondary button--icon modal__body__step__back">
              <i class="fa-light fa-arrow-left"></i>
            </button>
          </Transition>
          <div class="modal__body__step__body modal__body__step__body--list">
            <p class="modal__body__step__header-info">{{ $t("macro.add.select_entities") }}</p>
            <EntityListFiltered
              @change-filter="changeFilter"
              @reload-entities="reloadEntities"
              @change-assigned-entities="changeAssignedEntities"
              @change-page="changePage"
              @change-per-page="changePerPage"
              :all-entities="filteredEntities"
              :instances="instances"
              :pagination="pagination"
              :has-action-buttons="false"
              :has-quick-options="true"
              :has-dropdown-menu="false"
              :parent="'add-macro'"
              :exclude-entity-types="excludedEntityTypes"
            />
          </div>
        </div>
      </Transition>
      <template #footer>
        <button v-if="activeStep == 1" @click="goToStep(2)" class="button button--primary button--min-w">
          <template v-if="clone">{{ $t("ui.done") }}</template>
          <template v-else>{{ $t("ui.next") }}</template>
        </button>
        <button v-else-if="activeStep == 2" @click="submitForm" class="button button--primary button--min-w">
          {{ $t("ui.done") }}
        </button>
      </template>
    </Modal>
  </Teleport>
</template>